import { ArrowBackIosRounded, Refresh } from '@mui/icons-material'
import { Divider, FormControlLabel, Switch, TextField } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { HEADER_HEIGHT, theme } from '../../helpers/theme'
import { fetchLineage } from '../../store/actionCreators'
import { truncateText } from '../../helpers/text'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MQTooltip from '../../components/core/tooltip/MQTooltip'
import MqText from '../../components/core/text/MqText'
import React from 'react'
import { useSelector } from 'react-redux'
import { IState } from '../../store/reducers'
import { iframeLightColor, borderWhite, iframeLightBlockTitle } from '../../helpers/theme'

interface ActionBarProps {
  nodeType: 'DATASET' | 'JOB'
  fetchLineage: typeof fetchLineage
  depth: number
  setDepth: (depth: number) => void
  isCompact: boolean
  setIsCompact: (isCompact: boolean) => void
  isFull: boolean
  setIsFull: (isFull: boolean) => void
}

export const ActionBar = ({
  nodeType,
  fetchLineage,
  depth,
  setDepth,
  isCompact,
  setIsCompact,
  isFull,
  setIsFull,
}: ActionBarProps) => {
  const { namespace, name } = useParams()
  const navigate = useNavigate()
  const i18next = require('i18next')
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme: iframeTheme } = useSelector((state: IState) => state.iframe)
  return (
    <Box
      sx={{
        borderBottomWidth: 2,
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderStyle: 'dashed',
      }}
      display={'flex'}
      height={HEADER_HEIGHT - 1}
      justifyContent={'space-between'}
      alignItems={'center'}
      px={2}
      borderColor={iframeTheme === 'dark' ? theme.palette.secondary.main : borderWhite}
    >
      <Box display={'flex'} alignItems={'center'}>
        <MQTooltip title={nodeType === 'JOB' ? i18next.t('lineage.back_to_jobs') : i18next.t('lineage.back_to_datasets')}>
          <IconButton
            size={'small'}
            sx={{ mr: 2, color: iframeTheme === 'dark' ? iframeLightColor : iframeLightBlockTitle }}
            onClick={() => navigate(nodeType === 'JOB' ? '/' : '/datasets')}
          >
            <ArrowBackIosRounded fontSize={'small'} />
          </IconButton>
        </MQTooltip>
        <MqText heading>{nodeType === 'JOB' ? i18next.t('search.jobs') : i18next.t('search.datasets')}</MqText>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.mode')}</MqText>
          <MqText font={'mono'}>{i18next.t('lineage.table_level')}</MqText>
        </Box>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.namespace')}</MqText>
          <MqText font={'mono'}>
            {namespace ? truncateText(namespace, 40) : 'Unknown namespace name'}
          </MqText>
        </Box>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.name')}</MqText>
          <MqText font={'mono'}>{name ? truncateText(name, 40) : 'Unknown dataset name'}</MqText>
        </Box>
      </Box>
      <Box display={'flex'} alignItems={'center'}>
        <MQTooltip title={i18next.t('datasets_route.refresh')}>
          <IconButton
            sx={{ mr: 2 }}
            color={'primary'}
            size={'small'}
            onClick={() => {
              if (namespace && name) {
                fetchLineage(nodeType, namespace, name, depth)
              }
            }}
          >
            <Refresh fontSize={'small'} />
          </IconButton>
        </MQTooltip>
        <TextField
          id='column-level-depth'
          type='number'
          inputProps={{ min: 0 }}
          label={i18next.t('lineage.graph_depth_title')}
          variant='outlined'
          size='small'
          sx={{ width: '80px', mr: 2,
            '& .MuiOutlinedInput-root': {
              color: iframeTheme === 'dark' ? iframeLightColor : theme.palette.secondary.main,
              '& fieldset': {
                borderColor: iframeTheme === 'dark' ? 'rgba(255,255,255,0.23)' : borderWhite,
              },
              '&:hover fieldset': {
                borderColor: iframeTheme === 'dark' ? iframeLightColor : '#ddd',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              }
            },
            '& .MuiInputLabel-root': {
              color: iframeTheme === 'dark' ? 'rgba(171,177,189)' : theme.palette.secondary.main
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
           }}
          value={depth}
          onChange={(e) => {
            setDepth(isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value))
            searchParams.set('depth', e.target.value)
            setSearchParams(searchParams)
          }}
        />
        <Box display={'flex'} flexDirection={'column'}>
          <FormControlLabel
            control={
              <Switch
                size={'small'}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: iframeTheme === 'dark' ? 'iframeLightColor' : 'rgba(128, 128, 128, 0.5)',
                  }
                }}
                value={isFull}
                defaultChecked={searchParams.get('isFull') === 'true'}
                onChange={(_, checked) => {
                  setIsFull(checked)
                  searchParams.set('isFull', checked.toString())
                  setSearchParams(searchParams)
                }}
              />
            }
            label={<MqText font={'mono'}>{i18next.t('lineage.full_graph_label')}</MqText>}
          />
          <FormControlLabel
            control={
              <Switch
                size={'small'}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: iframeTheme === 'dark' ? 'iframeLightColor' : 'rgba(128, 128, 128, 0.5)',
                  }
                }}
                value={isCompact}
                defaultChecked={searchParams.get('isCompact') === 'true'}
                onChange={(_, checked) => {
                  setIsCompact(checked)
                  searchParams.set('isCompact', checked.toString())
                  setSearchParams(searchParams)
                }}
              />
            }
            label={<MqText font={'mono'}>{i18next.t('lineage.compact_nodes')}</MqText>}
          />
        </Box>
      </Box>
    </Box>
  )
}
